import { useCallback, useState, type DragEvent } from "react";

/** The drop target that means "this vendor line pairs with nothing". */
export const UNPAIRED_TRAY = "__tray__";

/**
 * What a dragged vendor line can be dropped on: an e-Manifest line number, or
 * the unpaired tray.
 */
export type PairingDropTarget = string;

export interface PairingDnd {
  /** The vendor line being dragged, or null. */
  dragging: string | null;
  /** The target the pointer is currently over, or null. */
  over: PairingDropTarget | null;
  startDrag: (event: DragEvent<HTMLElement>, vendorLine: string) => void;
  endDrag: () => void;
  dragOver: (event: DragEvent<HTMLElement>, target: PairingDropTarget) => void;
  dragLeave: (target: PairingDropTarget) => void;
  drop: (event: DragEvent<HTMLElement>, target: PairingDropTarget) => void;
}

/**
 * The drag half of the matching screen: which vendor line is in the hand and
 * which slot it is over.
 *
 * Native HTML5 drag and drop, the same way ScoutDashboardManageCharts does it,
 * because there is no drag library in this repo and one interaction does not
 * justify adding one. The dragged line is read from state rather than from
 * `dataTransfer`, which browsers only let a drop handler read: `setData` is
 * still called so a drag that leaves the window behaves like a normal one.
 *
 * Holds no pairing state of its own. It says what was dropped where and the
 * caller decides what that means, so the choices stay in one place and this
 * stays testable as the visual layer it is.
 *
 * Drag is never the only way in. Every row keeps its select, which is the path
 * a keyboard takes; nothing here is reachable by keyboard and nothing here is
 * the only way to record an answer.
 */
export function useWasteLinePairingDnd({
  disabled,
  onPlace,
  onUnpair,
}: {
  /** No dragging while a mutation is in flight. */
  disabled: boolean;
  onPlace: (vendorLine: string, eManifestLine: string) => void;
  onUnpair: (vendorLine: string) => void;
}): PairingDnd {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<PairingDropTarget | null>(null);

  const startDrag = useCallback(
    (event: DragEvent<HTMLElement>, vendorLine: string) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      setDragging(vendorLine);
      setOver(null);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", vendorLine);
    },
    [disabled]
  );

  const endDrag = useCallback(() => {
    setDragging(null);
    setOver(null);
  }, []);

  // Without preventDefault the browser refuses the drop, so this is what makes
  // a slot a slot.
  const dragOver = useCallback(
    (event: DragEvent<HTMLElement>, target: PairingDropTarget) => {
      if (dragging == null) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setOver(target);
    },
    [dragging]
  );

  const dragLeave = useCallback((target: PairingDropTarget) => {
    setOver((previous) => (previous === target ? null : previous));
  }, []);

  const drop = useCallback(
    (event: DragEvent<HTMLElement>, target: PairingDropTarget) => {
      event.preventDefault();
      const vendorLine = dragging;
      setDragging(null);
      setOver(null);
      if (vendorLine == null || disabled) return;
      if (target === UNPAIRED_TRAY) {
        onUnpair(vendorLine);
        return;
      }
      onPlace(vendorLine, target);
    },
    [disabled, dragging, onPlace, onUnpair]
  );

  return { dragging, over, startDrag, endDrag, dragOver, dragLeave, drop };
}
